/**
 * CodeConversationsView — the Code tab.
 *
 * The Code tab is a list of CONVERSATIONS across every coding group
 * (Claude, Codex and Gemini), not a list of group
 * rows. Mark's coding surface is one inbox: compose picks the agent, each row
 * says which agent owns it, and one tap archives.
 *
 * Sections:
 *   - Conversations: open personal topics, filtered by Done, Live, Snoozed or Long
 *   - Done footer: collapsed personal archive, then collapsed agent archive
 *
 * Rows are `GroupTopicRow` (the same row the automation groups use) with the
 * agent badge in its `leadingBadge` slot and the always-visible left action
 * rail turned on, so archive/reopen is one tap with no long-press.
 *
 * The per-group `GroupListView` is NOT reachable for these chats: ChatPage
 * redirects a coding-group root to this tab (see `codeGroupRootRedirect`).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { clsx } from 'clsx'
import { ChevronDown, ChevronRight, Archive, MessageSquarePlus } from 'lucide-react'
import {
  useTopics, useCloseTopic, useReopenTopic, type Topic, type WorkMonitor,
} from '../lib/api'
import {
  CODE_AGENTS, chatTarget, codeArchiveDestination, isAgentTopic, type CodeAgent,
} from '../lib/chatNavigation'
import { draftTargetKey, isVisibleDraft, useUi } from '../store/ui'
import { outboxTopicKey, useOutboxPendingScopes } from '../lib/outboxPending'
import { codeConversationState, matchesCodeConversationFilter, isLongCodeConversation, type CodeConversationFilter } from '../lib/codeConversationFilters'
import { withChatListReturn } from '../lib/chatNavigation'
import { useChatListReturn, useChatListSection } from '../lib/chatListReturn'
import AgentBadge from './AgentBadge'
import GroupTopicRow from './GroupTopicRow'
import UndoToast from './UndoToast'

type CodeRow = {
  chatId: string
  agent: CodeAgent
  label: string
  topic: Topic
}

const [CLAUDE_CODE, CODEX_CODE, GEMINI_CODE] = CODE_AGENTS

function lastActivityTs(topic: Topic): number {
  return topic.last_message_ts ?? topic.created_at
}

export default function CodeConversationsView({
  filter, onFilter, longOnly, onLong, workMonitor, workMonitorError, onRetryMonitor,
}: {
  filter: CodeConversationFilter
  onFilter: (filter: CodeConversationFilter) => void
  longOnly: boolean
  onLong: () => void
  workMonitor: WorkMonitor | undefined
  workMonitorError: Error | null
  onRetryMonitor: () => void
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const returnTo = useChatListReturn()
  // One `useTopics` per coding group. CODE_AGENTS is a frozen literal, so the
  // hook count is constant across renders — destructured explicitly rather than
  // mapped so that stays true by construction.
  const showArchives = filter === 'done' && !longOnly
  const claudeTopics = useTopics(CLAUDE_CODE.chatId, { includeOldDone: showArchives })
  const codexTopics = useTopics(CODEX_CODE.chatId, { includeOldDone: showArchives })
  const geminiTopics = useTopics(GEMINI_CODE.chatId, { includeOldDone: showArchives })
  const closeClaude = useCloseTopic(CLAUDE_CODE.chatId)
  const closeCodex = useCloseTopic(CODEX_CODE.chatId)
  const closeGemini = useCloseTopic(GEMINI_CODE.chatId)
  const reopenClaude = useReopenTopic(CLAUDE_CODE.chatId)
  const reopenCodex = useReopenTopic(CODEX_CODE.chatId)
  const reopenGemini = useReopenTopic(GEMINI_CODE.chatId)

  const draftsByChat = useUi((s) => s.draftsByChat)
  const laptop = useUi((s) => s.effectiveWidth >= 1024)
  const { topicKeys: outboxTopicKeys } = useOutboxPendingScopes()

  const [archiveOpen, toggleArchive] = useChatListSection('code-archive')
  const [agentTopicsOpen, toggleAgentTopics] = useChatListSection('code-agent-topics')
  const [agentArchiveOpen, toggleAgentArchive] = useChatListSection('code-agent-archive')
  const [actionError, setActionError] = useState<string | null>(null)
  const [undo, setUndo] = useState<{ agent: CodeAgent; topicId: string; message: string } | null>(null)

  const closeFor = useCallback(
    (agent: CodeAgent) => ({ claude: closeClaude, codex: closeCodex, gemini: closeGemini })[agent],
    [closeClaude, closeCodex, closeGemini],
  )
  const reopenFor = useCallback(
    (agent: CodeAgent) => ({ claude: reopenClaude, codex: reopenCodex, gemini: reopenGemini })[agent],
    [reopenClaude, reopenCodex, reopenGemini],
  )

  const rows = useMemo<CodeRow[]>(() => {
    const collected: CodeRow[] = []
    for (const [source, meta] of [
      [claudeTopics.data, CLAUDE_CODE] as const,
      [codexTopics.data, CODEX_CODE] as const,
      [geminiTopics.data, GEMINI_CODE] as const,
    ]) {
      for (const topic of source ?? []) {
        collected.push({ chatId: meta.chatId, agent: meta.agent, label: meta.label, topic })
      }
    }
    return collected
  }, [claudeTopics.data, codexTopics.data, geminiTopics.data])

  const visible = useMemo(() => rows.filter((row) => !isAgentTopic(row.topic)), [rows])

  const active = useMemo(() => (
    visible
      .filter((r) => !r.topic.closed)
      .sort((a, b) => lastActivityTs(b.topic) - lastActivityTs(a.topic))
  ), [visible])

  const archived = useMemo(() => (
    visible
      .filter((r) => r.topic.closed)
      .sort((a, b) => (b.topic.closed_at ?? 0) - (a.topic.closed_at ?? 0))
  ), [visible])

  const stateFor = (row: CodeRow) => codeConversationState(
    row.topic, outboxTopicKeys.has(outboxTopicKey(row.chatId, row.topic.id)),
  )
  const matchesFilter = (row: CodeRow, view: CodeConversationFilter) => matchesCodeConversationFilter(
    row.topic, view, outboxTopicKeys.has(outboxTopicKey(row.chatId, row.topic.id)),
  )
  const longRows = active.filter((row) => isLongCodeConversation(
    row.chatId, row.topic, workMonitor?.items ?? [], workMonitor?.now ?? Date.now() / 1000,
    outboxTopicKeys.has(outboxTopicKey(row.chatId, row.topic.id)),
  ))
  const showLongFilter = longRows.length > 0
  const showingLong = longOnly && showLongFilter
  const displayed = showingLong ? longRows : active.filter((row) => matchesFilter(row, filter))
  const filterButtons = [
    { value: 'done', label: 'Done', count: active.filter((row) => matchesFilter(row, 'done')).length },
    { value: 'live', label: 'Live', count: active.filter((row) => matchesFilter(row, 'live')).length },
    { value: 'snooze', label: 'Snoozed', count: active.filter((row) => matchesFilter(row, 'snooze')).length },
  ] satisfies { value: CodeConversationFilter; label: string; count: number }[]

  const agentActive = useMemo(() => rows
    .filter((row) => isAgentTopic(row.topic) && !row.topic.closed)
    .sort((a, b) => lastActivityTs(b.topic) - lastActivityTs(a.topic)), [rows])
  const agentArchived = useMemo(() => rows
    .filter((row) => isAgentTopic(row.topic) && row.topic.closed)
    .sort((a, b) => (b.topic.closed_at ?? 0) - (a.topic.closed_at ?? 0)), [rows])

  const openRow = useCallback((row: CodeRow, options?: { focusComposer?: boolean }) => {
    const href = withChatListReturn(chatTarget(row.chatId, row.topic.id, { parentTab: 'code' }), returnTo)
    navigate(options?.focusComposer ? `${href}&focus_composer=1` : href)
  }, [navigate, returnTo])

  const archiveRow = useCallback(async (row: CodeRow): Promise<boolean> => {
    setActionError(null)
    const destination = codeArchiveDestination(
      new URL(`${location.pathname}${location.search}`, 'https://agentchat.local'),
      { chatId: row.chatId, topicId: row.topic.id },
      (isAgentTopic(row.topic) ? agentActive : active).map((candidate) => ({
        chatId: candidate.chatId,
        topicId: candidate.topic.id,
      })),
    )
    try {
      // Coding conversations have no Todoist task behind them, so the close is
      // topic-only.
      await closeFor(row.agent).mutateAsync({ topic_id: row.topic.id, closeTodoist: false })
      setUndo({
        agent: row.agent,
        topicId: row.topic.id,
        message: `"${row.topic.title || '(untitled)'}" archived`,
      })
      if (destination) navigate(destination, { replace: true })
      return true
    } catch {
      setActionError(`Could not archive "${row.topic.title || '(untitled)'}".`)
      return false
    }
  }, [active, agentActive, closeFor, location.pathname, location.search, navigate])

  const reopenRow = useCallback((row: CodeRow) => {
    setActionError(null)
    reopenFor(row.agent).mutate({ topic_id: row.topic.id, reopenTodoist: false }, {
      onError: () => setActionError(
        `Could not reopen "${row.topic.title || '(untitled)'}". It stays archived.`,
      ),
    })
  }, [reopenFor])

  const renderRow = (row: CodeRow, section: 'active' | 'snoozed' | 'done') => {
    const draftKey = draftTargetKey(row.chatId, row.topic.id)
    const draft = draftsByChat[draftKey]
    return (
      <GroupTopicRow
        key={`${row.chatId}:${row.topic.id}`}
        chatId={row.chatId}
        topic={row.topic}
        draft={isVisibleDraft(draft) ? draft : null}
        section={section}
        inlineQuickActions
        compact={laptop}
        leadingBadge={<AgentBadge agent={row.agent} size={24} sessionRef={row.topic.session_ref} />}
        onOpen={() => openRow(row)}
        onSnoozePick={() => openRow(row)}
        archiveStyle
        onMarkDone={() => archiveRow(row)}
        onReopen={() => reopenRow(row)}
      />
    )
  }

  const loading = claudeTopics.isLoading || codexTopics.isLoading || geminiTopics.isLoading
  const topicsError = claudeTopics.error || codexTopics.error || geminiTopics.error

  useEffect(() => {
    if (longOnly && !showLongFilter && !loading && !topicsError && workMonitor && !workMonitorError) {
      onLong()
    }
  }, [longOnly, showLongFilter, loading, topicsError, workMonitor, workMonitorError, onLong])

  return (
    <div className="flex-1">
      <nav aria-label="Code conversation filters" className={clsx(
        'sticky top-0 z-10 grid gap-1 bg-stone-50 px-3 py-2 dark:bg-stone-950',
        showLongFilter ? 'grid-cols-4' : 'grid-cols-3',
      )}>
        {filterButtons.map((button) => (
          <button
            key={button.value}
            type="button"
            aria-pressed={!showingLong && filter === button.value}
            onClick={() => onFilter(button.value)}
            className={clsx(
              'inline-flex min-h-10 min-w-0 flex-wrap items-center justify-center gap-1 rounded-lg px-1 py-1 text-xs font-medium',
              !showingLong && filter === button.value
                ? 'bg-blue-600 text-white'
                : 'text-stone-600 hover:bg-stone-200/70 dark:text-stone-300 dark:hover:bg-stone-800/70',
            )}
          >
            {button.label} <span className="opacity-80">{button.count}</span>
          </button>
        ))}
        {showLongFilter && <button
          type="button"
          aria-pressed={showingLong}
          title="Show work running over 30 minutes"
          onClick={onLong}
          className={clsx(
            'inline-flex min-h-10 min-w-0 flex-wrap items-center justify-center gap-1 rounded-lg px-1 py-1 text-xs font-medium',
            showingLong ? 'bg-blue-600 text-white' : 'text-stone-600 hover:bg-stone-200/70 dark:text-stone-300 dark:hover:bg-stone-800/70',
          )}
        >
          Long <span className="opacity-80">{longRows.length}</span>
        </button>}
      </nav>
      {(claudeTopics.error || codexTopics.error || geminiTopics.error || (longOnly && workMonitorError)) && (
        <div role="alert" className="px-3 py-2 text-sm text-red-700 dark:text-red-300">
          Could not refresh conversations. Showing available data.
          <button type="button" className="ml-2 underline" onClick={() => {
            void claudeTopics.refetch()
            void codexTopics.refetch()
            void geminiTopics.refetch()
            if (longOnly) onRetryMonitor()
          }}>Retry</button>
        </div>
      )}
      {actionError && (
        <div role="alert" className="px-3 py-2 text-[13px] text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/40 border-b border-red-200 dark:border-red-900">
          {actionError}
        </div>
      )}

      <section>
        <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-stone-500">
          Conversations <span className="opacity-75">· {displayed.length}</span>
        </div>
        {displayed.length === 0 && !loading && (
          <div className="grid place-items-center px-6 py-14 text-center">
            <MessageSquarePlus className="w-9 h-9 mb-3 text-stone-400" aria-hidden />
            <div className="text-base font-medium mb-1">{active.length === 0 ? 'No coding conversations yet' : 'No conversations match this filter'}</div>
            <div className="text-sm text-stone-500">
              Tap the button below to start one with Claude or Codex.
            </div>
          </div>
        )}
        {displayed.map((row) => renderRow(row, row.topic.closed ? 'done' : stateFor(row) === 'snooze' ? 'snoozed' : 'active'))}
      </section>

      {agentActive.length > 0 && (
        <section aria-label="Agent topics">
          <button
            type="button"
            onClick={toggleAgentTopics}
            aria-expanded={agentTopicsOpen}
            aria-controls="agent-topics-list"
            className="flex min-h-11 w-full items-center gap-1.5 px-3 py-2 text-xs text-stone-500 hover:bg-stone-100/70 dark:hover:bg-stone-800/40"
          >
            {agentTopicsOpen
              ? <ChevronDown className="w-3.5 h-3.5" aria-hidden />
              : <ChevronRight className="w-3.5 h-3.5" aria-hidden />}
            Agent topics <span className="opacity-75">· {agentActive.length} active</span>
          </button>
          {agentTopicsOpen && (
            <div id="agent-topics-list">
              <p className="px-3 pb-2 text-xs text-stone-500">
                Work opened by agents. Kept separate from your conversations.
              </p>
              {agentActive.map((row) => renderRow(row, 'active'))}
            </div>
          )}
        </section>
      )}

      {showArchives && archived.length > 0 && (
        <section aria-label="Archived conversations">
          <button
            type="button"
            onClick={toggleArchive}
            aria-expanded={archiveOpen}
            aria-controls="code-conversations-archive"
            className="flex min-h-11 w-full items-center gap-1.5 px-3 py-2 text-xs text-stone-500 hover:bg-stone-100/70 dark:hover:bg-stone-800/40"
          >
            {archiveOpen
              ? <ChevronDown className="w-3.5 h-3.5" aria-hidden />
              : <ChevronRight className="w-3.5 h-3.5" aria-hidden />}
            <Archive className="w-3.5 h-3.5" aria-hidden />
            Archived <span className="opacity-75">· {archived.length}</span>
          </button>
          {archiveOpen && (
            <div id="code-conversations-archive">
              {archived.map((row) => renderRow(row, 'done'))}
            </div>
          )}
        </section>
      )}

      {showArchives && agentArchived.length > 0 && (
        <section aria-label="Archived agent topics">
          <button
            type="button"
            onClick={toggleAgentArchive}
            aria-expanded={agentArchiveOpen}
            aria-controls="agent-topics-archive"
            className="flex min-h-11 w-full items-center gap-1.5 px-3 py-2 text-xs text-stone-500 hover:bg-stone-100/70 dark:hover:bg-stone-800/40"
          >
            {agentArchiveOpen
              ? <ChevronDown className="w-3.5 h-3.5" aria-hidden />
              : <ChevronRight className="w-3.5 h-3.5" aria-hidden />}
            <Archive className="w-3.5 h-3.5" aria-hidden />
            Archived agent topics <span className="opacity-75">· {agentArchived.length}</span>
          </button>
          {agentArchiveOpen && (
            <div id="agent-topics-archive">
              {agentArchived.map((row) => renderRow(row, 'done'))}
            </div>
          )}
        </section>
      )}

      {undo && (
        <UndoToast
          message={undo.message}
          onUndo={async () => {
            await reopenFor(undo.agent).mutateAsync({
              topic_id: undo.topicId,
              reopenTodoist: false,
            })
            setUndo(null)
          }}
          onDismiss={() => setUndo(null)}
        />
      )}
    </div>
  )
}
